import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus } from 'lucide-react';
import { EditActionBar } from '../shared/EditActionBar';
import type {
  Driver03Data, RevenueSummaryRow, RevenueComparisonRow,
  RevenueSummarySection,
} from '../../../api/apiTypesConductRisk';
import { useConductRiskDriver } from '../../../api/hooks/useConductRiskDriver';
import { formatHighlights } from '../../../utils/Format';

const TIPOLOGIA_LABEL: Record<string, string> = {
  RETAIL:           'Retail',
  EXCLUSIVE:        'Exclusive',
  IMPRESE:          'Imprese',
  DIGITALE_REMOTA:  'Digitale Remota',
  TERZO_SETTORE:    'Terzo Settore',
  AGRIBUSINESS:     'Agribusiness',
};

// Etichette sempre in MAIUSCOLO: sia quelle mappate (es. "Digitale Remota")
// sia le chiavi non mappate che arrivano grezze dal BE (es. IMPACT, REMOTO),
// così la resa è uniforme in tutta la vista.
const tipoLabel = (k: string) => (TIPOLOGIA_LABEL[k] ?? k).toUpperCase();

/* ─── Helpers ────────────────────────────────────────────── */
// Valori formattati a 2 decimali fissi (standard banking). Niente arrotondamento
// "aggressivo" come Math.round: i decimali della fonte sono preservati fino al
// secondo, sufficiente per importi monetari e percentuali.
const fmtNum2 = (n: number) =>
  new Intl.NumberFormat('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
const fmtN    = (n: number) =>
  new Intl.NumberFormat('it-IT').format(n);             // conteggi interi (filiali)
const fmtE    = fmtNum2;                                // monetari in €
const fmtPct1 = (n: number) => `${fmtNum2(n)}%`;
const signPct = (n: number) => `${n >= 0 ? '+' : ''}${fmtNum2(n)}`;

const numCls = 'w-24 text-right bg-white text-[#1B365D] border border-[#1B365D]/20 rounded px-1.5 py-0.5 text-xs focus:outline-none focus:border-[#1B365D]/50 tabular-nums';
const txtCls = 'w-full min-w-[7rem] bg-white border border-[#1B365D]/20 rounded px-1.5 py-0.5 text-xs text-[#1B365D] focus:outline-none focus:border-[#1B365D]/50';

/* ─── Normalizzazione difensiva ──────────────────────────────
 * Il BE puo' restituire `rows`/`totals` come null per una sezione (segmento
 * senza dati) benche' il tipo li dichiari non-nullable. Senza guardia il render
 * dei totali (section.totals.gross_revenue) crasha. Normalizzo a strutture
 * sicure al caricamento, cosi' tutto il resto del componente resta invariato. */
const emptySummaryRow = (regional_division = 'Totale'): RevenueSummaryRow => ({
  regional_division, branches: 0, gross_revenue: 0, budget: 0, budget_achievement_pct: 0, revenue_per_branch: 0,
});
const emptyComparisonRow = (segment = 'Totale'): RevenueComparisonRow => ({
  segment, revenue: 0, prior_revenue: 0, revenue_share_pct: 0, prior_revenue_share_pct: 0, revenue_delta_pct: 0, revenue_share_delta_pp: 0,
});

const normalizeDriver03 = (d: Driver03Data): Driver03Data => {
  const revenue_summary: Record<string, RevenueSummarySection> = {};
  Object.entries(d.revenue_summary ?? {}).forEach(([k, sec]) => {
    revenue_summary[k] = {
      rows:   sec?.rows   ?? [],
      totals: sec?.totals ?? emptySummaryRow(),
    };
  });
  const cmp = d.revenue_comparison;
  return {
    ...d,
    revenue_summary,
    revenue_comparison: {
      rows:   cmp?.rows   ?? [],
      totals: cmp?.totals ?? emptyComparisonRow(),
    },
  };
};

/* ─── Tabella Riepilogo Ricavi ───────────────────────────── */
interface TableSummaryProps {
  rows:               RevenueSummaryRow[];
  totals:             RevenueSummaryRow;
  isEditing:          boolean;
  onUpdate:           (i: number, field: keyof RevenueSummaryRow, raw: string) => void;
  onUpdateName:       (i: number, value: string) => void;
  onUpdateTotals:     (field: keyof RevenueSummaryRow, raw: string) => void;
  onUpdateTotalsName: (value: string) => void;
}

const TableSummary =({ rows, totals, isEditing, onUpdate, onUpdateName, onUpdateTotals, onUpdateTotalsName }: TableSummaryProps) => (
  <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-gray-50 text-gray-400 uppercase text-[10px] tracking-wide">
            <th className="text-left px-5 py-2.5 font-semibold">Direzione</th>
            <th className="text-right px-4 py-2.5 font-semibold">Filiali</th>
            <th className="text-right px-4 py-2.5 font-semibold">Ricavi Lordi (€)</th>
            <th className="text-right px-4 py-2.5 font-semibold">Budget (€)</th>
            <th className="text-right px-4 py-2.5 font-semibold">% Ragg. BDG</th>
            <th className="text-right px-4 py-2.5 font-semibold">Pro capite fil. (€)</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {rows.map((row, i) => (
            <motion.tr
              key={i}
              initial={{ opacity: 0, x: -4 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.04 }}
              className="hover:bg-gray-50/60 transition-colors"
            >
              <td className="px-5 py-3 font-medium text-[#1B365D]">
                {isEditing ? <input type="text" className={txtCls} value={row.regional_division} onChange={e => onUpdateName(i, e.target.value)} /> : row.regional_division}
              </td>
              <td className="px-4 py-2.5 text-right text-gray-600">
                {isEditing ? <input type="number" className={numCls} value={row.branches} onChange={e => onUpdate(i, 'branches', e.target.value)} /> : fmtN(row.branches)}
              </td>
              <td className="px-4 py-2.5 text-right font-semibold text-gray-700">
                {isEditing ? <input type="number" className={numCls} value={row.gross_revenue} onChange={e => onUpdate(i, 'gross_revenue', e.target.value)} /> : fmtE(row.gross_revenue)}
              </td>
              <td className="px-4 py-2.5 text-right text-gray-400">
                {isEditing ? <input type="number" className={numCls} value={row.budget} onChange={e => onUpdate(i, 'budget', e.target.value)} /> : fmtE(row.budget)}
              </td>
              <td className="px-4 py-3 text-right">
                {isEditing
                  ? <input type="number" className={numCls} value={row.budget_achievement_pct} onChange={e => onUpdate(i, 'budget_achievement_pct', e.target.value)} />
                  : <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                      row.budget_achievement_pct >= 100 ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                      : row.budget_achievement_pct >= 95 ? 'bg-amber-50 text-amber-600 border-amber-200'
                      : 'bg-red-50 text-red-600 border-red-200'
                    }`}>
                      {fmtPct1(row.budget_achievement_pct)}
                    </span>}
              </td>
              <td className="px-4 py-3 text-right text-gray-600">
                {isEditing ? <input type="number" className={numCls} value={row.revenue_per_branch} onChange={e => onUpdate(i, 'revenue_per_branch', e.target.value)} /> : fmtE(row.revenue_per_branch)}
              </td>
            </motion.tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="bg-[#1B365D] text-white text-[11px] font-semibold">
            <td className="px-5 py-3">
              {isEditing ? <input type="text" className={txtCls} value={totals.regional_division} onChange={e => onUpdateTotalsName(e.target.value)} /> : totals.regional_division}
            </td>
            <td className="px-4 py-3 text-right">
              {isEditing ? <input type="number" className={numCls} value={totals.branches} onChange={e => onUpdateTotals('branches', e.target.value)} /> : fmtN(totals.branches)}
            </td>
            <td className="px-4 py-3 text-right">
              {isEditing ? <input type="number" className={numCls} value={totals.gross_revenue} onChange={e => onUpdateTotals('gross_revenue', e.target.value)} /> : fmtE(totals.gross_revenue)}
            </td>
            <td className="px-4 py-3 text-right">
              {isEditing ? <input type="number" className={numCls} value={totals.budget} onChange={e => onUpdateTotals('budget', e.target.value)} /> : fmtE(totals.budget)}
            </td>
            <td className="px-4 py-3 text-right">
              {isEditing ? <input type="number" className={numCls} value={totals.budget_achievement_pct} onChange={e => onUpdateTotals('budget_achievement_pct', e.target.value)} /> : fmtPct1(totals.budget_achievement_pct)}
            </td>
            <td className="px-4 py-3 text-right">
              {isEditing ? <input type="number" className={numCls} value={totals.revenue_per_branch} onChange={e => onUpdateTotals('revenue_per_branch', e.target.value)} /> : fmtE(totals.revenue_per_branch)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
);

/* ─── Main ───────────────────────────────────────────────── */
const RicaviContainer = ({ reportId }: { reportId: string }) => {
  const { data: remoteData, loading, error, save, isReadOnly } = useConductRiskDriver<Driver03Data>(reportId, 3);

  const [data,         setData]         = useState<Driver03Data | null>(null);
  const [savedData,    setSavedData]    = useState<Driver03Data | null>(null);
  const [isEditing,    setIsEditing]    = useState(false);
  const [activeOutput, setActiveOutput] = useState(0);
  const [activeTipo,   setActiveTipo]   = useState(0);

  useEffect(() => {
    if (remoteData) {
      const normalized = normalizeDriver03(remoteData);
      setData(normalized);
      setSavedData(normalized);
    }
  }, [remoteData]);

  const handleOutputChange = (i: number) => { setActiveOutput(i); setActiveTipo(0); };
  const handleAnnulla = () => { if (savedData) setData(savedData); setIsEditing(false); };
  const handleSalva   = async () => {
    if (!data) return;
    await save(data);
    setSavedData(data);
    setIsEditing(false);
  };

  /* ─── Riepilogo: righe (numerico / nome direzione) ─── */
  const updateSummaryRow = (tk: string, i: number, field: keyof RevenueSummaryRow, raw: string) => {
    const val = parseFloat(raw);
    if (isNaN(val)) return;
    setData(prev => (prev ? {
      ...prev,
      revenue_summary: {
        ...prev.revenue_summary,
        [tk]: {
          ...prev.revenue_summary[tk],
          rows: prev.revenue_summary[tk].rows.map((r, ri) => ri === i ? { ...r, [field]: val } : r),
        },
      },
    } : prev));
  };
  const updateSummaryRowName = (tk: string, i: number, value: string) => {
    setData(prev => (prev ? {
      ...prev,
      revenue_summary: {
        ...prev.revenue_summary,
        [tk]: {
          ...prev.revenue_summary[tk],
          rows: prev.revenue_summary[tk].rows.map((r, ri) => ri === i ? { ...r, regional_division: value } : r),
        },
      },
    } : prev));
  };
  const addSummaryRow = (tk: string) => {
    setData(prev => (prev ? {
      ...prev,
      revenue_summary: {
        ...prev.revenue_summary,
        [tk]: {
          ...prev.revenue_summary[tk],
          rows: [...prev.revenue_summary[tk].rows, emptySummaryRow()],
        },
      },
    } : prev));
  };

  /* ─── Riepilogo: totali (numerico / nome) ─── */
  const updateSummaryTotals = (tk: string, field: keyof RevenueSummaryRow, raw: string) => {
    const val = parseFloat(raw);
    if (isNaN(val)) return;
    setData(prev => (prev ? {
      ...prev,
      revenue_summary: {
        ...prev.revenue_summary,
        [tk]: { ...prev.revenue_summary[tk], totals: { ...prev.revenue_summary[tk].totals, [field]: val } },
      },
    } : prev));
  };
  const updateSummaryTotalsName = (tk: string, value: string) => {
    setData(prev => (prev ? {
      ...prev,
      revenue_summary: {
        ...prev.revenue_summary,
        [tk]: { ...prev.revenue_summary[tk], totals: { ...prev.revenue_summary[tk].totals, regional_division: value } },
      },
    } : prev));
  };

  /* ─── Confronto: righe (numerico / nome segmento) ─── */
  const updateComparisonRow = (i: number, field: keyof RevenueComparisonRow, raw: string) => {
    const val = parseFloat(raw);
    if (isNaN(val)) return;
    setData(prev => (prev ? {
      ...prev,
      revenue_comparison: {
        ...prev.revenue_comparison,
        rows: prev.revenue_comparison.rows.map((r, ri) => ri === i ? { ...r, [field]: val } : r),
      },
    } : prev));
  };
  const updateComparisonRowName = (i: number, value: string) => {
    setData(prev => (prev ? {
      ...prev,
      revenue_comparison: {
        ...prev.revenue_comparison,
        rows: prev.revenue_comparison.rows.map((r, ri) => ri === i ? { ...r, segment: value } : r),
      },
    } : prev));
  };
  const addComparisonRow = () => {
    setData(prev => (prev ? {
      ...prev,
      revenue_comparison: {
        ...prev.revenue_comparison,
        rows: [...prev.revenue_comparison.rows, emptyComparisonRow()],
      },
    } : prev));
  };

  /* ─── Confronto: totali ─── */
  const updateComparisonTotals = (field: keyof RevenueComparisonRow, raw: string) => {
    const val = parseFloat(raw);
    if (isNaN(val)) return;
    setData(prev => (prev ? {
      ...prev,
      revenue_comparison: { ...prev.revenue_comparison, totals: { ...prev.revenue_comparison.totals, [field]: val } },
    } : prev));
  };

  const updateSummaryHighlights = (value: string) => {
    setData(prev => (prev ? { ...prev, revenue_summary_highlights: value } : prev));
  };

  const updateComparisonHighlights = (value: string) => {
    setData(prev => (prev ? { ...prev, revenue_comparison_highlights: value } : prev));
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-12 h-12 border-[5px] border-gray-200 border-t-[#1B365D] rounded-full animate-spin" />
      </div>
    );
  }
  if (error && !data) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>
      </div>
    );
  }
  if (!data) {
    return <div className="p-6 text-sm text-gray-400">Nessun dato disponibile per questo driver.</div>;
  }

  const tipoKeys = Object.keys(data.revenue_summary);
  const tipoKey  = tipoKeys[activeTipo] ?? tipoKeys[0];
  // Fallback se la sezione manca del tutto (revenue_summary vuoto): evita il
  // crash su section.rows/section.totals quando non ci sono segmenti.
  const section: RevenueSummarySection = data.revenue_summary[tipoKey] ?? { rows: [], totals: emptySummaryRow() };
  const cmp        = data.revenue_comparison;
  const cmpTotals  = cmp.totals;

  return (
    <div className="p-6 space-y-4">

      {/* Tabs + edit bar */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1 p-1 bg-gray-50 rounded-xl border border-gray-100 w-fit">
          {[`Riepilogo ricavi ${data.period}`, `Confronto ${data.prior_period} → ${data.period}`].map((label, i) => (
            <button
              key={i}
              onClick={() => handleOutputChange(i)}
              className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                activeOutput === i
                  ? 'bg-white text-[#1B365D] shadow-sm border border-gray-100'
                  : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <EditActionBar isEditing={isEditing} onProcedi={() => setIsEditing(true)} onAnnulla={handleAnnulla} onSalva={handleSalva} disabled={isReadOnly} />
      </div>

      <AnimatePresence mode="wait">
        {activeOutput === 0 ? (
          <motion.div
            key="o1"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="space-y-4"
          >
            {/* Tipologia tabs */}
            <div className="flex gap-1 flex-wrap">
              {tipoKeys.map((k, i) => (
                <button
                  key={k}
                  onClick={() => setActiveTipo(i)}
                  className={`px-3 py-1 rounded-lg text-[11px] font-semibold transition-all border ${
                    activeTipo === i
                      ? 'bg-[#1B365D] text-white border-[#1B365D]'
                      : 'bg-white text-gray-400 border-gray-100 hover:border-[#1B365D]/20 hover:text-gray-600'
                  }`}
                >
                  {tipoLabel(k)}
                </button>
              ))}
            </div>

            {/* KPI strip */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white rounded-xl border border-gray-100 p-5 text-center">
                <div className="text-2xl font-bold text-[#1B365D]">{fmtE(section.totals.gross_revenue)} €</div>
                <div className="text-[11px] text-gray-400 mt-1">Ricavi lordi {data.period}</div>
              </div>
              <div className="bg-white rounded-xl border border-gray-100 p-5 text-center">
                <div className={`text-2xl font-bold ${section.totals.budget_achievement_pct >= 100 ? 'text-emerald-600' : 'text-amber-600'}`}>
                  {fmtPct1(section.totals.budget_achievement_pct)}
                </div>
                <div className="text-[11px] text-gray-400 mt-1">Raggiungimento budget</div>
              </div>
              <div className="bg-white rounded-xl border border-gray-100 p-5 text-center">
                <div className="text-2xl font-bold text-[#1B365D]">{fmtN(section.totals.branches)}</div>
                <div className="text-[11px] text-gray-400 mt-1">Filiali in scope</div>
              </div>
            </div>

            {/* Table */}
            <AnimatePresence mode="wait">
              <motion.div key={activeTipo} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <TableSummary
                  rows={section.rows}
                  totals={section.totals}
                  isEditing={isEditing}
                  onUpdate={(i, field, raw) => updateSummaryRow(tipoKey, i, field, raw)}
                  onUpdateName={(i, value) => updateSummaryRowName(tipoKey, i, value)}
                  onUpdateTotals={(field, raw) => updateSummaryTotals(tipoKey, field, raw)}
                  onUpdateTotalsName={value => updateSummaryTotalsName(tipoKey, value)}
                />
              </motion.div>
            </AnimatePresence>

            {isEditing && (
              <div className="flex justify-end mt-2">
                <button
                  type="button"
                  onClick={() => addSummaryRow(tipoKey)}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-[#1B365D] border border-[#1B365D]/30 hover:bg-[#1B365D]/5 rounded-lg px-3 py-1.5 transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" /> Aggiungi nuovo
                </button>
              </div>
            )}

            {/* Highlights */}
            <div className="px-4 py-3 rounded-xl bg-[#1B365D]/5 border border-[#1B365D]/10 text-xs text-[#1B365D]/80 leading-relaxed">
              <span className="font-semibold">Highlights: </span>
              {isEditing
                ? <textarea className="w-full mt-1 p-2 text-xs text-[#1B365D]/80 leading-relaxed border border-[#1B365D]/20 rounded-lg resize-y focus:outline-none focus:border-[#1B365D]/40 bg-white/70 min-h-[100px]" value={data.revenue_summary_highlights} onChange={e => updateSummaryHighlights(e.target.value)} />
                : <span className="whitespace-pre-wrap">{formatHighlights(data.revenue_summary_highlights)}</span>}
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="o2"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="space-y-4"
          >
            {/* KPI strip */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white rounded-xl border border-gray-100 p-5 text-center">
                <div className="text-2xl font-bold text-[#1B365D]">{fmtE(cmpTotals.prior_revenue)} €</div>
                <div className="text-[11px] text-gray-400 mt-1">Totale ricavi {data.prior_period}</div>
              </div>
              <div className="bg-white rounded-xl border border-gray-100 p-5 text-center">
                <div className="text-2xl font-bold text-[#1B365D]">{fmtE(cmpTotals.revenue)} €</div>
                <div className="text-[11px] text-gray-400 mt-1">Totale ricavi {data.period}</div>
              </div>
              <div className="bg-white rounded-xl border border-gray-100 p-5 text-center">
                <div className={`text-2xl font-bold ${cmpTotals.revenue_delta_pct >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                  {signPct(cmpTotals.revenue_delta_pct)}%
                </div>
                <div className="text-[11px] text-gray-400 mt-1">Variazione biennale</div>
              </div>
            </div>

            {/* Comparison table */}
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100">
                <p className="text-xs font-semibold text-[#1B365D]">
                  Confronto ricavi {data.prior_period} → {data.period} per territorio commerciale (€)
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50 text-gray-400 uppercase text-[10px] tracking-wide">
                      <th className="text-left  px-5 py-2.5 font-semibold">Territorio Commerciale</th>
                      <th className="text-right px-4 py-2.5 font-semibold">{data.prior_period} Consuntivo</th>
                      <th className="text-right px-4 py-2.5 font-semibold">{data.prior_period} Inc. %</th>
                      <th className="text-right px-4 py-2.5 font-semibold">{data.period} Consuntivo</th>
                      <th className="text-right px-4 py-2.5 font-semibold">{data.period} Inc. %</th>
                      <th className="text-right px-4 py-2.5 font-semibold">Diff. Cons. %</th>
                      <th className="text-right px-4 py-2.5 font-semibold">Diff. Inc. pp</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {cmp.rows.map((row, i) => (
                      <motion.tr
                        key={i}
                        initial={{ opacity: 0, x: -4 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.05 }}
                        className="hover:bg-gray-50/60 transition-colors"
                      >
                        <td className="px-5 py-3 font-medium text-[#1B365D]">
                          {isEditing ? <input type="text" className={txtCls} value={row.segment} onChange={e => updateComparisonRowName(i, e.target.value)} /> : tipoLabel(row.segment)}
                        </td>
                        <td className="px-4 py-2.5 text-right text-gray-500">
                          {isEditing ? <input type="number" className={numCls} value={row.prior_revenue} onChange={e => updateComparisonRow(i, 'prior_revenue', e.target.value)} /> : fmtE(row.prior_revenue)}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-400">
                          {isEditing ? <input type="number" className={numCls} value={row.prior_revenue_share_pct} onChange={e => updateComparisonRow(i, 'prior_revenue_share_pct', e.target.value)} /> : fmtPct1(row.prior_revenue_share_pct)}
                        </td>
                        <td className="px-4 py-2.5 text-right font-semibold text-gray-700">
                          {isEditing ? <input type="number" className={numCls} value={row.revenue} onChange={e => updateComparisonRow(i, 'revenue', e.target.value)} /> : fmtE(row.revenue)}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-600">
                          {isEditing ? <input type="number" className={numCls} value={row.revenue_share_pct} onChange={e => updateComparisonRow(i, 'revenue_share_pct', e.target.value)} /> : fmtPct1(row.revenue_share_pct)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {isEditing
                            ? <input type="number" className={numCls} value={row.revenue_delta_pct} onChange={e => updateComparisonRow(i, 'revenue_delta_pct', e.target.value)} />
                            : <span className={`font-semibold ${row.revenue_delta_pct >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                                {signPct(row.revenue_delta_pct)}%
                              </span>}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {isEditing
                            ? <input type="number" className={numCls} value={row.revenue_share_delta_pp} onChange={e => updateComparisonRow(i, 'revenue_share_delta_pp', e.target.value)} />
                            : <span className={`font-semibold ${row.revenue_share_delta_pp >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                                {signPct(row.revenue_share_delta_pp)}
                              </span>}
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-[#1B365D] text-white text-[11px] font-semibold">
                      <td className="px-5 py-3">Totale</td>
                      <td className="px-4 py-3 text-right">
                        {isEditing ? <input type="number" className={numCls} value={cmpTotals.prior_revenue} onChange={e => updateComparisonTotals('prior_revenue', e.target.value)} /> : fmtE(cmpTotals.prior_revenue)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {isEditing ? <input type="number" className={numCls} value={cmpTotals.prior_revenue_share_pct} onChange={e => updateComparisonTotals('prior_revenue_share_pct', e.target.value)} /> : fmtPct1(cmpTotals.prior_revenue_share_pct)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {isEditing ? <input type="number" className={numCls} value={cmpTotals.revenue} onChange={e => updateComparisonTotals('revenue', e.target.value)} /> : fmtE(cmpTotals.revenue)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {isEditing ? <input type="number" className={numCls} value={cmpTotals.revenue_share_pct} onChange={e => updateComparisonTotals('revenue_share_pct', e.target.value)} /> : fmtPct1(cmpTotals.revenue_share_pct)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {isEditing ? <input type="number" className={numCls} value={cmpTotals.revenue_delta_pct} onChange={e => updateComparisonTotals('revenue_delta_pct', e.target.value)} /> : `${signPct(cmpTotals.revenue_delta_pct)}%`}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {isEditing ? <input type="number" className={numCls} value={cmpTotals.revenue_share_delta_pp} onChange={e => updateComparisonTotals('revenue_share_delta_pp', e.target.value)} /> : '—'}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {isEditing && (
              <div className="flex justify-end mt-2">
                <button
                  type="button"
                  onClick={() => addComparisonRow()}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-[#1B365D] border border-[#1B365D]/30 hover:bg-[#1B365D]/5 rounded-lg px-3 py-1.5 transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" /> Aggiungi nuovo
                </button>
              </div>
            )}

            {/* Highlights */}
            <div className="px-4 py-3 rounded-xl bg-[#1B365D]/5 border border-[#1B365D]/10 text-xs text-[#1B365D]/80 leading-relaxed">
              <span className="font-semibold">Highlights: </span>
              {isEditing
                ? <textarea className="w-full mt-1 p-2 text-xs text-[#1B365D]/80 leading-relaxed border border-[#1B365D]/20 rounded-lg resize-y focus:outline-none focus:border-[#1B365D]/40 bg-white/70 min-h-[100px]" value={data.revenue_comparison_highlights} onChange={e => updateComparisonHighlights(e.target.value)} />
                : <span className="whitespace-pre-wrap">{formatHighlights(data.revenue_comparison_highlights)}</span>}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RicaviContainer;
